import dayjs from "dayjs";

export interface GatePrinterStatus {
  inError: boolean;
  paperNearEnd: boolean;
  paperJam: boolean;
  coverOpened: boolean;
  paperMissing: boolean;
  ticketInOutput: boolean;
}

export type PrinterFault = "inError" | "paperJam" | "paperMissing" | "coverOpened" | "paperNearEnd";

/** Most serious first. `ticketInOutput` is not a fault. */
const PRINTER_FAULTS: PrinterFault[] = ["inError", "paperJam", "paperMissing", "coverOpened", "paperNearEnd"];

/** The printer problems to show; empty when the printer is fine or unreported. */
export function getPrinterFaults(printer: GatePrinterStatus | null | undefined): PrinterFault[] {
  if (!printer) return [];
  return PRINTER_FAULTS.filter((fault) => printer[fault]);
}

/** Local time (24h) a gate was put on hold, e.g. "02:24"; `undefined` when the timestamp is unusable. */
export function formatHeldSince(since: string | undefined): string | undefined {
  if (!since) return undefined;
  const date = dayjs(since);
  return date.isValid() ? date.format("HH:mm") : undefined;
}

/**
 * What a status read means in plain facts. These are the meanings measured on real gates (2026-10-31), not
 * ParkNet's own labels for the fields:
 * - `closingLoopActive` is on while the barrier is physically up: it came on 2-3 s after a remote Open with no car,
 *   stayed on for a barrier left up all day, and in car passes came on after arming and went off once the car left.
 * - `armingLoopActive` is on while a vehicle is at the terminal.
 * `gateActive` never moved during any Open or car pass and is unexplained, so it is deliberately not used.
 */
export function getGateReadings(status: { armingLoopActive: boolean; closingLoopActive: boolean }): { barrierUp: boolean; vehicleAtTerminal: boolean } {
  return { barrierUp: status.closingLoopActive, vehicleAtTerminal: status.armingLoopActive };
}

/** The part of an intercom extension that says which gate it opens. */
export interface IntercomExtensionGate {
  name: string;
  gate?: { name?: string; deviceId: string; gateId: string };
}

/**
 * The names of the other gates that read through the same device. A status is read per device, so a reading taken
 * for this gate may be about any of them. Another extension on this gate's own device and gate ids is the same gate,
 * not another one; each name is listed once. The name is the one the agent sees for that gate: its gate name, else
 * its extension name.
 */
export function getSharedGateNames(gate: { deviceId: string; gateId: string } | undefined, extensions: readonly IntercomExtensionGate[] | undefined): string[] {
  if (!gate || !extensions) return [];
  const names = extensions
    .filter((other) => other.gate && String(other.gate.deviceId) === String(gate.deviceId) && String(other.gate.gateId) !== String(gate.gateId))
    .map((other) => other.gate?.name || other.name)
    .filter(Boolean);
  return [...new Set(names)];
}
